import { retryCancelablePromise } from '../core/utils/retryCancelablePromise';
export class BranchService {
    constructor(httpRequest) {
        Object.defineProperty(this, "httpRequest", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: httpRequest
        });
    }
    /**
     * Get all branches
     * @returns BranchList OK.
     * @throws ApiError
     */
    getAllBranches({ orgId, projectId, cookie, filter, sort = '-id', limit = 10, after, before, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'GET', url: '/orgs/{orgId}/projects/{projectId}/branches', path: {
                'orgId': orgId,
                'projectId': projectId,
            }, headers: {
                'cookie': cookie,
            }, query: {
                'filter': filter,
                'sort': sort,
                'limit': limit,
                'after': after,
                'before': before,
            }, errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Create branch
     * @returns Branch OK.
     * @throws ApiError
     */
    createBranch({ orgId, projectId, requestBody, cookie, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/orgs/{orgId}/projects/{projectId}/branches', path: {
                'orgId': orgId,
                'projectId': projectId,
            }, headers: {
                'cookie': cookie,
            }, body: requestBody, mediaType: 'application/json', errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Update branch
     * @returns Branch OK.
     * @throws ApiError
     */
    updateBranch({ orgId, projectId, branchName, requestBody, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'PUT', url: '/orgs/{orgId}/projects/{projectId}/branches/{branchName}', path: {
                'orgId': orgId,
                'projectId': projectId,
                'branchName': branchName,
            }, body: requestBody, mediaType: 'application/json', errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Delete branch
     * @returns void
     * @throws ApiError
     */
    deleteBranch({ orgId, projectId, branchName, force, cookie, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'DELETE', url: '/orgs/{orgId}/projects/{projectId}/branches/{branchName}', path: {
                'orgId': orgId,
                'projectId': projectId,
                'branchName': branchName,
            }, headers: {
                'cookie': cookie,
            }, query: {
                'force': force,
            }, errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Get branch
     * @returns Branch OK.
     * @throws ApiError
     */
    getBranch({ orgId, projectId, branchName, cookie, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'GET', url: '/orgs/{orgId}/projects/{projectId}/branches/{branchName}', path: {
                'orgId': orgId,
                'projectId': projectId,
                'branchName': branchName,
            }, headers: {
                'cookie': cookie,
            }, errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Merge branches
     * @returns any OK.
     * @throws ApiError
     */
    mergeBranches({ orgId, projectId, branchName, branchToMerge, force, userId, rebase, autoMerge, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/orgs/{orgId}/projects/{projectId}/branches/{branchName}/merge', path: {
                'orgId': orgId,
                'projectId': projectId,
                'branchName': branchName,
            }, query: {
                'branchToMerge': branchToMerge,
                'force': force,
                'userId': userId,
                'rebase': rebase,
                'autoMerge': autoMerge,
            }, errors: {
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                409: `Conflict.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Get Git diff with context
     * @returns string OK.
     * @throws ApiError
     */
    gitDiff({ orgId, projectId, branchName, context = '1', compareWith, latestMainCommitHash, mergeCommitHash, nameOnly, nameStatusOnly, path, commitIsh, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'GET', url: '/orgs/{orgId}/projects/{projectId}/branches/{branchName}/diff', path: {
                'orgId': orgId,
                'projectId': projectId,
                'branchName': branchName,
            }, query: {
                'context': context,
                'compareWith': compareWith,
                'latestMainCommitHash': latestMainCommitHash,
                'mergeCommitHash': mergeCommitHash,
                'nameOnly': nameOnly,
                'nameStatusOnly': nameStatusOnly,
                'path': path,
                'commitIsh': commitIsh,
            }, errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Rebase Branch
     * @returns any OK.
     * @throws ApiError
     */
    branchRebase({ orgId, projectId, branchName, requestBody, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/orgs/{orgId}/projects/{projectId}/branches/{branchName}/rebase', path: {
                'orgId': orgId,
                'projectId': projectId,
                'branchName': branchName,
            }, body: requestBody, mediaType: 'application/json', errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Sync Branch
     * Merge default branch into target branch. Conflicted files become uncommitted changes.
     * @returns any OK.
     * @throws ApiError
     */
    branchSync({ orgId, projectId, branchName, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/orgs/{orgId}/projects/{projectId}/branches/{branchName}/sync', path: {
                'orgId': orgId,
                'projectId': projectId,
                'branchName': branchName,
            }, errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Get Common Ancestor Commit Hash with Main Branch
     * @returns string OK.
     * @throws ApiError
     */
    commonMainCommit({ orgId, projectId, branchName, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'GET', url: '/orgs/{orgId}/projects/{projectId}/branches/{branchName}/common-main-commit', path: {
                'orgId': orgId,
                'projectId': projectId,
                'branchName': branchName,
            }, errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Get Branch Commits
     * @returns any OK.
     * @throws ApiError
     */
    branchCommits({ orgId, projectId, branchName, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'GET', url: '/orgs/{orgId}/projects/{projectId}/branches/{branchName}/commits', path: {
                'orgId': orgId,
                'projectId': projectId,
                'branchName': branchName,
            }, errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Get Branch Merge Conflicts
     * @returns MergeConflicts OK.
     * @throws ApiError
     */
    getBranchMergeConflicts({ orgId, projectId, branchName, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'GET', url: '/orgs/{orgId}/projects/{projectId}/branches/{branchName}/merge-conflicts', path: {
                'orgId': orgId,
                'projectId': projectId,
                'branchName': branchName,
            }, errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Commit branch
     * @returns Branch OK.
     * @throws ApiError
     */
    commitChanges({ orgId, projectId, branchName, commitHash, requestBody, cookie, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/orgs/{orgId}/projects/{projectId}/branches/{branchName}/commits/{commitHash}/commit', path: {
                'orgId': orgId,
                'projectId': projectId,
                'branchName': branchName,
                'commitHash': commitHash,
            }, headers: {
                'cookie': cookie,
            }, body: requestBody, mediaType: 'application/json', errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
}
//# sourceMappingURL=BranchService.js.map